import React, { FunctionComponent, useEffect, useMemo } from "react";
import { bindActionCreators, Dispatch } from "redux";
import {
  addExistingBehaviorEcgToDataPeriod,
  getDataPeriods,
  getEvidenceInDataPeriod,
  openConductInterviewModal,
  openPickDataSourceModal,
  patchDataPeriod,
} from "../../../../../store/onboarding/actions";
import { connect, useSelector } from "react-redux";
import { ApplicationState } from "../../../../../store";
import {
  DataPeriod,
  EvidencePeriodData,
} from "../../../../../store/onboarding/types";
import TeacherInviteIndividuallyModal from "../TeacherInviteIndividuallyModal";
import usePrevProps from "../../../../../utils/hooks/usePrevProps";
import StartWorkingContainer from "../../third-step/start-working/StartWorkingContainer";
import useUserRole from "../../../../../utils/hooks/useUserRole";
import IntroPanel from "../../third-step/start-working/IntroPanel";

type PropsFromState = {
  evidencePeriodData?: EvidencePeriodData;
  isEvidenceFetching: boolean;
  isLoading: {
    addExistingBehaviorEcgToDataPeriod: boolean;
    uploadEvidenceData: boolean;
    addAssessmentToDataPeriod: boolean;
    uploadAssessmentData: boolean;
    uploadUndefinedAssessmentData: boolean;
  };
  errors: {
    uploadAssessmentData?: string | Array<any>;
  };
  hasEmptyData: boolean;
};

type DispatchProps = {
  patchDataPeriod: (
    dataPeriodId: number,
    dataPeriod: Partial<DataPeriod>
  ) => any;
  getEvidenceInDataPeriod: (dataPeriodId?: number) => any;
  getDataPeriods: () => any;
  openPickDataSourceModal: () => any;
  addExistingBehaviorEcgToDataPeriod: (dataPeriodId?: number) => any;
  openConductInterviewModal: typeof openConductInterviewModal;
};

type Props = PropsFromState & DispatchProps;

const FormsEvidence: FunctionComponent<Props> = (props: Props) => {
  const { isDistrictOwner, isTeacher } = useUserRole();
  const prevProps = usePrevProps({
    uploadEvidenceData: props.isLoading.uploadEvidenceData,
    addAssessmentToDataPeriod: props.isLoading.addAssessmentToDataPeriod,
    uploadAssessmentData: props.isLoading.uploadAssessmentData,
    uploadUndefinedAssessmentData:
      props.isLoading.uploadUndefinedAssessmentData,
  });

  const permissionSettings = useSelector((state: ApplicationState) => state.auth.userInfo!);
  const isPermissionSettingsPresent = useMemo(() => {
      let settingPresent: boolean | undefined = false;
      if(!isDistrictOwner) {
        settingPresent = permissionSettings?.settings_present;
        if(settingPresent) {
          // RC-2657
          // if all dropdown is empty then then assume setting is present
          // if atleast one dropdown has value then assume setting is present
          settingPresent = 
              permissionSettings?.share_all_data 
              || (permissionSettings?.allowed_assigned_teachers_ids.length == 0
                    && permissionSettings?.allowed_grades?.length == 0
                    && permissionSettings?.allowed_schools?.length == 0 )
              || (permissionSettings?.allowed_assigned_teachers_ids.length > 0
                  || permissionSettings?.allowed_grades?.length > 0
                  || permissionSettings?.allowed_schools?.length > 0
                );
        }
      } else {
        settingPresent = true;
      }
      return settingPresent;
    }, [permissionSettings]);

  useEffect(() => {
    if (
      (prevProps?.addAssessmentToDataPeriod &&
        !props.isLoading.addAssessmentToDataPeriod)
    ) {
      props.getDataPeriods().then((dataPeriods: Array<DataPeriod>) => {
        if (dataPeriods.length && !dataPeriods.some((dp) => dp.is_selected)) {
          props.patchDataPeriod(dataPeriods[0].id, { is_selected: true, all_data_period_ids: dataPeriods.map((dp) => dp?.id) || [] });
          // .then(() => props.getEvidenceInDataPeriod(dataPeriods[0].id));
        }
      });
    }
  }, [prevProps, props.isLoading]);

  useEffect(() => {
    if (
      ((prevProps?.uploadEvidenceData && !props.isLoading.uploadEvidenceData) ||
      (prevProps?.uploadUndefinedAssessmentData &&
        !props.isLoading.uploadUndefinedAssessmentData) ||
      (prevProps?.uploadAssessmentData &&
        !props.isLoading.uploadAssessmentData
        && !props.errors.uploadAssessmentData))
      && !isDistrictOwner
    ) {
      props.getDataPeriods().then((dataPeriods: Array<DataPeriod>) => {
        if (dataPeriods.length && !dataPeriods.some((dp) => dp.is_selected)) {
          props.patchDataPeriod(dataPeriods[0].id, { is_selected: true, all_data_period_ids: dataPeriods.map((dp) => dp?.id) || [] });
          // .then(() => props.getEvidenceInDataPeriod(dataPeriods[0].id));
        }
      });
    }
  }, [prevProps, props.isLoading]);

  if (props.isEvidenceFetching) {
    return null;
  }
  // RC-2657 District Admin: isPermissionSettingsPresent is always true (ignored), only hasEmptyData gates the container.
  // RC-2657 Coach/Teacher: isPermissionSettingsPresent is checked first and passed down so StartWorkingContainer/IntroPanel
  // can render the correct "Get Started" button and conditional copy, but the container itself is only shown
  // once the data period has no data (hasEmptyData).
  return props.hasEmptyData ? (
    <div className="dataTooltipContainer">
      <StartWorkingContainer isPermissionSettingsPresent={isPermissionSettingsPresent} />
      {isDistrictOwner ? <TeacherInviteIndividuallyModal /> : ''}
    </div>
  ) : null;
};

const mapStateToProps = ({ onboarding }: ApplicationState): PropsFromState => {
  return {
    isLoading: {
      addExistingBehaviorEcgToDataPeriod:
        onboarding.isLoading.addExistingBehaviorEcgToDataPeriod,
      uploadEvidenceData: onboarding.isLoading.uploadEvidenceData,
      addAssessmentToDataPeriod: onboarding.isLoading.addAssessmentToDataPeriod,
      uploadAssessmentData: onboarding.isLoading.uploadAssessmentData,
      uploadUndefinedAssessmentData:
        onboarding.isLoading.uploadUndefinedAssessmentData,
    },
    errors: {
      uploadAssessmentData: onboarding.errors.uploadAssessmentData,
    },
    evidencePeriodData: onboarding.evidencePeriodData,
    isEvidenceFetching: onboarding.isLoading.getEvidenceInDatePeriod,
    hasEmptyData: (
      onboarding.evidencePeriodData === undefined ||
      onboarding.evidencePeriodData.evidence_column_groups.length === 0
    ),
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps =>
  bindActionCreators(
    {
      getEvidenceInDataPeriod: getEvidenceInDataPeriod,
      patchDataPeriod: patchDataPeriod,
      getDataPeriods: getDataPeriods,
      openPickDataSourceModal: openPickDataSourceModal,
      addExistingBehaviorEcgToDataPeriod: addExistingBehaviorEcgToDataPeriod,
      openConductInterviewModal: openConductInterviewModal,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(FormsEvidence);
